/**
 * Widget i18n — typed nested messages, zero runtime deps.
 *
 * Components call `messages()` and access the tree by property: `m.pill.cta`.
 * TypeScript enforces keys against the `Messages` contract (locales/en as the
 * source of truth), so typos and missing translations fail at build time.
 */

import { en } from "./locales/en";
import type { Messages } from "./types";

const locales = { en } satisfies Record<string, Messages>;
type LocaleCode = keyof typeof locales;

const fallbackLocale: LocaleCode = "en";

let current: Messages = en;
let currentCode: LocaleCode = fallbackLocale;

function isSupported(code: string): code is LocaleCode {
  return code in locales;
}

/**
 * Select the active locale from a BCP-47 tag (e.g. "es-MX" → "es").
 * Falls back to English for unknown codes; safe to call with `undefined`.
 */
export function initI18n(locale?: string): void {
  if (!locale) return;
  const code = locale.split("-")[0]?.toLowerCase();
  if (code && isSupported(code)) {
    current = locales[code];
    currentCode = code;
  }
}

/** Typed message tree for the active locale. */
export function messages(): Messages {
  return current;
}

/**
 * Language tag for the messages currently rendering. Goes on the widget's
 * mount point: `lang` crosses into the shadow root from the partner page, and
 * the casing rules behind `text-transform: uppercase` are language-dependent —
 * on a `tr`/`az` page "advertisement" would otherwise render as
 * "ADVERTİSEMENT" with a dotted capital I.
 */
export function currentLocale(): string {
  return currentCode;
}

export type { Messages };
